import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import analytic from '@global/utils/AdobeAnalytics';
import { useYupValidationResolver } from '@src/global/components/ActiveForm/utils';
import {
  BillingFormType,
  BillingInfoFormValuesType,
} from '@src/global/components/BillingInfoForm/types';
import { NotificationTypes } from '@src/global/components/Notification';
import { useNotification } from '@src/global/hooks/useNotification';
import { useAppDispatch, useAppSelector } from '@src/global/store/hooks';
import { updateBillingInformation } from '@src/global/store/slices/subscription/extraReducers';
import { subscriptionSelector } from '@src/global/store/slices/subscription/selectors';
import { billingInformationSchema } from '@src/pages/checkout/components/BillingInformation/rules';
import { AccessSubscriptionButton } from '../AccessSubscriptionButton';
import { BillingInfo } from '../BillingInfo';
import { ChangeBillingInfoForm } from '../ChangeBillingInfoForm';
import { PaymentInfoView } from '../PaymentInfo';
import { PaymentInfoChangeForm } from '../PaymentInfoChangeForm';
import { SubscriptionInfo } from '../SubscriptionInfo';
import { SubscriptionProduct } from '../SubscriptionProduct';

export const SubscriptionContainer = ({
  subscriptionUid,
}: {
  subscriptionUid: string;
}) => {
  const { t } = useTranslation();
  const { setShowNotification } = useNotification();

  const dispatch = useAppDispatch();

  const { error, notification, subscriptionsByUid } =
    useAppSelector(subscriptionSelector);

  const paymentUid = subscriptionsByUid[subscriptionUid].paymentUid;
  const billingInfo = subscriptionsByUid[subscriptionUid].billingInfo;
  const paymentInfo = subscriptionsByUid[subscriptionUid].paymentInfo;
  const orderPrice = subscriptionsByUid[subscriptionUid].orderPrice;
  const periodInfo = subscriptionsByUid[subscriptionUid].periodInfo;
  const product = subscriptionsByUid[subscriptionUid].product;
  const billingPlan = subscriptionsByUid[subscriptionUid].billingPlan;
  const subscriptionSiteId =
    subscriptionsByUid[subscriptionUid].subscriptionSiteId;
  const isCancelled = subscriptionsByUid[subscriptionUid].isCancelled;
  const lastBillBiId = subscriptionsByUid[subscriptionUid].lastBillBiId;

  const [isShowBillingInfoForm, setIsShowBillingInfoForm] = useState(false);
  const [isShowPaymentForm, setIsShowPaymentForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isCartButtonLoading, setIsCartButtonLoading] =
    useState<boolean>(false);

  const toggleShowBillingInfoForm = () => {
    setIsShowBillingInfoForm((prev) => !prev);
    setIsShowPaymentForm(false);
  };

  const toggleShowPaymentForm = () => {
    setIsShowPaymentForm((prev) => !prev);
    setIsShowBillingInfoForm(false);
  };

  const resolverSchema = billingInformationSchema();
  const rulesResolver = useYupValidationResolver(resolverSchema);

  const billingInfoFormMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: rulesResolver,
  });

  const billingInfoFormSubmitHandler = useCallback(
    async (formData: BillingInfoFormValuesType) => {
      setIsCartButtonLoading(true);
      let data = formData;
      if (!data) {
        data = billingInfoFormMethods.getValues();
      }

      analytic.dataLayerForm({
        formName: 'billingInformationChangeForm',
        formEvent: 'form submit',
      });

      dispatch(
        updateBillingInformation({
          t,
          cb: () => {
            setIsShowPaymentForm(false);
            setIsShowBillingInfoForm(false);
            setIsCartButtonLoading(false);
          },
          ...data,
          subscriptionUid,
          paymentUid,
          billingInfo,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (isShowPaymentForm) {
      return;
    }

    if (billingInfo) {
      resolverSchema
        .validate({
          CCname: billingInfo?.name,
          phone: billingInfo?.tel,
          address1: billingInfo?.addressOne,
          address2: billingInfo?.addressTwo,
          state: billingInfo?.state,
          city: billingInfo?.city,
          zip: billingInfo?.zip,
          country: billingInfo?.country?.iso2Code,
        })
        .then(() => {
          billingInfoFormMethods.trigger();
        })
        .catch(() => undefined);
    }
  }, [billingInfo, isShowBillingInfoForm, paymentMethod]);

  useEffect(() => {
    error && setShowNotification([NotificationTypes.error, error]);
    notification &&
      setShowNotification([NotificationTypes.success, notification]);
  }, [error, notification]);

  return (
    <div className="subscription__content-container">
      <div className="sub-information__row">
        <span className="sub-information__text_gray">
          {t('subDetails.sub.info.id', { biid: lastBillBiId })}
        </span>
      </div>
      <div className="subscription__item-wrapper">
        <SubscriptionProduct
          product={product}
          isActive={true}
          isCancelled={isCancelled}
          periodInfo={periodInfo}
          totalPrice={`${orderPrice.total.code}${orderPrice.total.value.toFixed(
            2
          )}`}
          subscriptionFrequency={billingPlan.bipBillAtRPIT}
          accessSubscriptionButton={
            <AccessSubscriptionButton
              setShowNotification={setShowNotification}
              subscriptionUid={subscriptionUid}
              wSiteId={subscriptionSiteId}
            />
          }
        />
      </div>

      <div className="subscription__content-wrapper">
        <div className="subscription__content">
          <SubscriptionInfo
            disabled={isCancelled}
            subscriptionUid={subscriptionUid}
            orderPrice={orderPrice}
            periodInfo={periodInfo}
            product={product}
            subscriptionFrequency={billingPlan.bipBillAtRPIT}
          />
          <div
            className="subscription__billing-wrapper"
            id="payment-form-anchor"
          >
            <span className="subscription__title subscription__title--billing">
              {t('subDetails.sub.billing.title')}
            </span>
            <div className="sub-information__column-container">
              <BillingInfo
                billingInfo={billingInfo}
                disabled={isCancelled}
                toggleOpen={toggleShowBillingInfoForm}
                isShow={isShowBillingInfoForm}
              />

              <PaymentInfoView
                paymentInfo={paymentInfo}
                disabled={isCancelled}
                toggleOpen={toggleShowPaymentForm}
                isShow={isShowPaymentForm}
              />
            </div>
          </div>
        </div>
      </div>

      {isShowPaymentForm ? (
        <div className="subscription__payment-form">
          <PaymentInfoChangeForm
            setPaymentMethod={setPaymentMethod}
            paymentMethod={paymentMethod}
            billingInfoFormSubmitHandler={billingInfoFormSubmitHandler}
            billingInfoFormMethods={billingInfoFormMethods}
            hidden={!isShowPaymentForm}
            subscriptionUid={subscriptionUid}
            handleClose={() => {
              setIsShowPaymentForm(false);
              billingInfoFormMethods.reset();
            }}
          />
        </div>
      ) : null}

      {isShowBillingInfoForm ? (
        <div className="subscription__billing-form">
          <ChangeBillingInfoForm
            formType={BillingFormType.ChangeBillingForm}
            methods={billingInfoFormMethods}
            onSubmit={billingInfoFormMethods.handleSubmit(
              billingInfoFormSubmitHandler
            )}
            isCartButtonLoading={isCartButtonLoading}
            setIsCartButtonLoading={setIsCartButtonLoading}
            billingInfo={billingInfo}
            setIsShow={(value: boolean) => setIsShowBillingInfoForm(value)}
            isShow={isShowBillingInfoForm}
            buttonId="btn-info-form-submit"
            buttonText={t('subDetails.sub.billing.billingEditCta.cta')}
          />
        </div>
      ) : null}
    </div>
  );
};
